import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingManagementApi } from '@/api/services/settingManagement';
import type {
  SendTestEmailInput,
  TimeZoneSettingsDto,
  UpdateEmailSettingsDto,
} from '@/api/types/settingManagement';

export const settingManagementKeys = {
  all: ['settingManagement'] as const,
  timeZone: ['settingManagement', 'timeZone'] as const,
  timezones: ['settingManagement', 'timezones'] as const,
  emailing: ['settingManagement', 'emailing'] as const,
};

export function useTimeZone() {
  return useQuery({
    queryKey: settingManagementKeys.timeZone,
    queryFn: () => settingManagementApi.getTimeZone(),
  });
}

export function useTimezones() {
  return useQuery({
    queryKey: settingManagementKeys.timezones,
    queryFn: () => settingManagementApi.getTimezones(),
    staleTime: Infinity,
  });
}

export function useUpdateTimeZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TimeZoneSettingsDto) => settingManagementApi.updateTimeZone(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingManagementKeys.timeZone }),
  });
}

export function useEmailSettings() {
  return useQuery({
    queryKey: settingManagementKeys.emailing,
    queryFn: () => settingManagementApi.getEmailSettings(),
  });
}

export function useUpdateEmailSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateEmailSettingsDto) => settingManagementApi.updateEmailSettings(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingManagementKeys.emailing }),
  });
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: (input: SendTestEmailInput) => settingManagementApi.sendTestEmail(input),
  });
}
